import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminPlanChangeRequest,
  AssignSubscriptionInput,
  BillingStatus,
  TenantLimitOverridesInput,
  TenantLimits,
  CollectionsSettings,
  CollectionMeta,
  CreatePlanChangeRequestInput,
  CreatePlanInput,
  ExtendTrialInput,
  GenerateInvoiceInput,
  Invoice,
  InvoiceCheckout,
  ListInvoicesQuery,
  PaymentConfig,
  PlanChangeCheckout,
  Plan,
  PlanChangeRequest,
  PlanChangeRequestStatus,
  PlanFeature,
  PlanWithFeatures,
  PublicPlan,
  ReviewPlanChangeRequestInput,
  Subscription,
  UpdatePlanInput,
  VerifyPaymentInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const PLANS_KEY = ['admin', 'plans'] as const;
const PLAN_FEATURES_KEY = ['admin', 'plan-features'] as const;
const SUBSCRIPTION_KEY = ['admin', 'subscription'] as const;
const INVOICES_KEY = ['admin', 'invoices'] as const;
const MY_BILLING_KEY = ['billing'] as const;
const TENANT_PLANS_KEY = ['billing', 'plans'] as const;
const PLAN_CHANGE_REQUESTS_KEY = ['billing', 'plan-change-requests'] as const;
const ADMIN_PLAN_CHANGE_REQUESTS_KEY = ['admin', 'plan-change-requests'] as const;

export interface InvoicePage {
  items: Invoice[];
  meta: CollectionMeta;
}

function toQuery(q: ListInvoicesQuery): Record<string, string | number | undefined> {
  return { page: q.page, pageSize: q.pageSize };
}

// --- Platform admin: plans ------------------------------------------------

export const listPlans = () => apiClient.get<Plan[]>('/admin/plans');
export const createPlan = (input: CreatePlanInput) => apiClient.post<Plan>('/admin/plans', input);
export const updatePlan = (planId: string, patch: UpdatePlanInput) =>
  apiClient.patch<Plan>(`/admin/plans/${planId}`, patch);

export function usePlans() {
  return useQuery<Plan[], ApiClientError>({ queryKey: PLANS_KEY, queryFn: listPlans });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation<Plan, ApiClientError, CreatePlanInput>({
    mutationFn: createPlan,
    onSuccess: () => qc.invalidateQueries({ queryKey: PLANS_KEY }),
  });
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation<Plan, ApiClientError, { planId: string; patch: UpdatePlanInput }>({
    mutationFn: ({ planId, patch }) => updatePlan(planId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: PLANS_KEY }),
  });
}

// --- Platform admin: per-plan feature matrix -------------------------------

export const getPlanFeatures = (planId: string) =>
  apiClient.get<PlanFeature[]>(`/admin/plans/${planId}/features`);
export const setPlanFeatures = (planId: string, features: PlanFeature[]) =>
  apiClient.put<PlanFeature[]>(`/admin/plans/${planId}/features`, { features });

export function usePlanFeatures(planId: string | undefined) {
  return useQuery<PlanFeature[], ApiClientError>({
    queryKey: [...PLAN_FEATURES_KEY, planId],
    queryFn: () => getPlanFeatures(planId!),
    enabled: Boolean(planId),
  });
}

export function useSetPlanFeatures(planId: string) {
  const qc = useQueryClient();
  return useMutation<PlanFeature[], ApiClientError, PlanFeature[]>({
    mutationFn: (features) => setPlanFeatures(planId, features),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...PLAN_FEATURES_KEY, planId] }),
  });
}

// --- Platform admin: a tenant's subscription + invoices -------------------

export const getTenantSubscription = (tenantId: string) =>
  apiClient.get<Subscription | null>(`/admin/tenants/${tenantId}/subscription`);
export const assignSubscription = (tenantId: string, input: AssignSubscriptionInput) =>
  apiClient.post<Subscription>(`/admin/tenants/${tenantId}/subscription`, input);
export const generateInvoice = (tenantId: string, input: GenerateInvoiceInput) =>
  apiClient.post<Invoice>(`/admin/tenants/${tenantId}/invoices`, input);
export const voidInvoice = (tenantId: string, invoiceId: string) =>
  apiClient.post<Invoice>(`/admin/tenants/${tenantId}/invoices/${invoiceId}/void`);

export async function listTenantInvoices(
  tenantId: string,
  q: ListInvoicesQuery = {},
): Promise<InvoicePage> {
  const res = await apiClient.getCollection<Invoice>(`/admin/tenants/${tenantId}/invoices`, {
    query: toQuery(q),
  });
  return { items: res.data, meta: res.meta };
}

export function useTenantSubscription(tenantId: string | undefined) {
  return useQuery<Subscription | null, ApiClientError>({
    queryKey: [...SUBSCRIPTION_KEY, tenantId],
    queryFn: () => getTenantSubscription(tenantId!),
    enabled: Boolean(tenantId),
  });
}

export function useAssignSubscription(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<Subscription, ApiClientError, AssignSubscriptionInput>({
    mutationFn: (input) => assignSubscription(tenantId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...SUBSCRIPTION_KEY, tenantId] });
      qc.invalidateQueries({ queryKey: [...INVOICES_KEY, tenantId] });
      // A new plan brings new limits and menus.
      qc.invalidateQueries({ queryKey: ['admin', 'limits', tenantId] });
      qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}

export const extendTrial = (tenantId: string, input: ExtendTrialInput) =>
  apiClient.patch<Subscription>(`/admin/tenants/${tenantId}/subscription/trial-end`, input);

export function useExtendTrial(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<Subscription, ApiClientError, ExtendTrialInput>({
    mutationFn: (input) => extendTrial(tenantId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...SUBSCRIPTION_KEY, tenantId] }),
  });
}

export function useTenantInvoices(tenantId: string | undefined, query: ListInvoicesQuery = {}) {
  return useQuery<InvoicePage, ApiClientError>({
    queryKey: [...INVOICES_KEY, tenantId, query],
    queryFn: () => listTenantInvoices(tenantId!, query),
    enabled: Boolean(tenantId),
  });
}

export function useGenerateInvoice(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<Invoice, ApiClientError, GenerateInvoiceInput>({
    mutationFn: (input) => generateInvoice(tenantId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...INVOICES_KEY, tenantId] }),
  });
}

export function useVoidInvoice(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<Invoice, ApiClientError, string>({
    mutationFn: (invoiceId) => voidInvoice(tenantId, invoiceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...INVOICES_KEY, tenantId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'billing-status', tenantId] });
    },
  });
}

/** Platform admin: record a bank transfer / UPI / cheque payment. */
export function useMarkInvoicePaid(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<Invoice, ApiClientError, { invoiceId: string; reference: string }>({
    mutationFn: ({ invoiceId, reference }) =>
      apiClient.post<Invoice>(`/admin/tenants/${tenantId}/invoices/${invoiceId}/mark-paid`, {
        reference,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...INVOICES_KEY, tenantId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'billing-status', tenantId] });
    },
  });
}

// --- Tenant-facing (own subscription + invoices, `billing.read`) ----------

export const getMySubscription = () => apiClient.get<Subscription | null>('/billing/subscription');

export async function listMyInvoices(q: ListInvoicesQuery = {}): Promise<InvoicePage> {
  const res = await apiClient.getCollection<Invoice>('/billing/invoices', { query: toQuery(q) });
  return { items: res.data, meta: res.meta };
}

export function useMySubscription(enabled = true) {
  return useQuery<Subscription | null, ApiClientError>({
    queryKey: [...MY_BILLING_KEY, 'subscription'],
    queryFn: getMySubscription,
    enabled,
  });
}

export function useMyInvoices(query: ListInvoicesQuery = {}) {
  return useQuery<InvoicePage, ApiClientError>({
    queryKey: [...MY_BILLING_KEY, 'invoices', query],
    queryFn: () => listMyInvoices(query),
  });
}

// --- Tenant-facing: plan catalogue + self-service upgrade requests --------

export const listPlansWithFeatures = () => apiClient.get<PlanWithFeatures[]>('/plans');

/** The public pricing catalogue - no login needed (pricing page, "Plans" menu). */
export function usePublicPlans() {
  return useQuery<PublicPlan[], ApiClientError>({
    queryKey: ['public-plans'],
    queryFn: () => apiClient.get<PublicPlan[]>('/public/plans'),
    staleTime: 5 * 60_000,
  });
}
export const createPlanChangeRequest = (input: CreatePlanChangeRequestInput) =>
  apiClient.post<PlanChangeRequest>('/plan-change-requests', input);
export const listPlanChangeRequests = async (): Promise<PlanChangeRequest[]> =>
  (
    await apiClient.getCollection<PlanChangeRequest>('/plan-change-requests', {
      query: { pageSize: 100 },
    })
  ).data;

export function usePlansWithFeatures() {
  return useQuery<PlanWithFeatures[], ApiClientError>({
    queryKey: TENANT_PLANS_KEY,
    queryFn: listPlansWithFeatures,
  });
}

export function usePlanChangeRequests() {
  return useQuery<PlanChangeRequest[], ApiClientError>({
    queryKey: PLAN_CHANGE_REQUESTS_KEY,
    queryFn: listPlanChangeRequests,
  });
}

export function useCreatePlanChangeRequest() {
  const qc = useQueryClient();
  return useMutation<PlanChangeRequest, ApiClientError, CreatePlanChangeRequestInput>({
    mutationFn: createPlanChangeRequest,
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_CHANGE_REQUESTS_KEY }),
  });
}

// --- Platform admin: cross-tenant plan-change-request review queue --------

export const listAdminPlanChangeRequests = async (
  status?: PlanChangeRequestStatus,
): Promise<AdminPlanChangeRequest[]> =>
  (
    await apiClient.getCollection<AdminPlanChangeRequest>('/admin/plan-change-requests', {
      query: { status, pageSize: 100 },
    })
  ).data;
export const reviewPlanChangeRequest = (requestId: string, input: ReviewPlanChangeRequestInput) =>
  apiClient.post<null>(`/admin/plan-change-requests/${requestId}/review`, input);

export function useAdminPlanChangeRequests(status?: PlanChangeRequestStatus) {
  return useQuery<AdminPlanChangeRequest[], ApiClientError>({
    queryKey: [...ADMIN_PLAN_CHANGE_REQUESTS_KEY, status],
    queryFn: () => listAdminPlanChangeRequests(status),
  });
}

export function useReviewPlanChangeRequest() {
  const qc = useQueryClient();
  return useMutation<
    null,
    ApiClientError,
    { requestId: string; input: ReviewPlanChangeRequestInput }
  >({
    mutationFn: ({ requestId, input }) => reviewPlanChangeRequest(requestId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ADMIN_PLAN_CHANGE_REQUESTS_KEY }),
  });
}

// --- Paying an invoice online (Razorpay Checkout, `billing.manage`) -------

interface RazorpaySuccess {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

interface RazorpayInstance {
  open: () => void;
  on: (event: 'payment.failed', handler: (r: { error?: { description?: string } }) => void) => void;
}

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => RazorpayInstance;
  }
}

const CHECKOUT_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';

/** Loads Razorpay's Checkout script once. */
function loadCheckout(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${CHECKOUT_SCRIPT}"]`);
    const script = existing ?? document.createElement('script');
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () =>
      reject(new Error('Could not load Razorpay. Check your connection and try again.')),
    );
    if (!existing) {
      script.src = CHECKOUT_SCRIPT;
      script.async = true;
      document.body.appendChild(script);
    }
  });
}

export const startInvoicePayment = (invoiceId: string) =>
  apiClient.post<InvoiceCheckout>(`/billing/invoices/${invoiceId}/pay`);
export const verifyInvoicePayment = (input: VerifyPaymentInput) =>
  apiClient.post<Invoice>('/billing/payments/verify', input);
export const startPlanChange = (planId: string) =>
  apiClient.post<PlanChangeCheckout>('/billing/plan-change', { planId });
const getPaymentConfig = () => apiClient.get<PaymentConfig>('/billing/payments/config');

/** Whether Razorpay is set up on the server (no "Pay now"/"Upgrade now" otherwise). */
export function usePaymentConfig() {
  return useQuery<PaymentConfig, ApiClientError>({
    queryKey: [...MY_BILLING_KEY, 'payment-config'],
    queryFn: getPaymentConfig,
    staleTime: 5 * 60_000,
  });
}

/**
 * "Upgrade now": issues a plan-change invoice, then pays it like
 * `usePayInvoice`. Paying switches the plan. Resolves with the paid invoice,
 * or `null` if Checkout was closed (the invoice stays open in the list).
 */
export function useUpgradePlan() {
  const qc = useQueryClient();
  return useMutation<Invoice | null, Error, string>({
    mutationFn: async (planId) => {
      const [checkout] = await Promise.all([startPlanChange(planId), loadCheckout()]);
      return runCheckout(checkout);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: MY_BILLING_KEY }),
  });
}

/**
 * "Pay now": creates the order on our server (amount from the invoice),
 * opens Razorpay Checkout, then confirms the payment with our server.
 * Resolves with the paid invoice; rejects with a readable message, or
 * `null` when the customer just closed the Checkout window.
 */
export function usePayInvoice() {
  const qc = useQueryClient();
  return useMutation<Invoice | null, Error, string>({
    mutationFn: async (invoiceId) => {
      const [checkout] = await Promise.all([startInvoicePayment(invoiceId), loadCheckout()]);
      return runCheckout(checkout);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: MY_BILLING_KEY }),
  });
}

/** Opens Razorpay Checkout for a server-created order and verifies the result. */
async function runCheckout(checkout: InvoiceCheckout): Promise<Invoice | null> {
  const Checkout = window.Razorpay;
  if (!Checkout) throw new Error('Could not load Razorpay. Please try again.');
  const response = await new Promise<RazorpaySuccess | null>((resolve, reject) => {
    const rzp = new Checkout({
      key: checkout.keyId,
      order_id: checkout.orderId,
      amount: checkout.amountPaise,
      currency: checkout.currency,
      name: checkout.businessName,
      description: checkout.description,
      prefill: checkout.prefill,
      handler: (r: RazorpaySuccess) => resolve(r),
      modal: { ondismiss: () => resolve(null) },
    });
    rzp.on('payment.failed', (r) =>
      reject(new Error(r.error?.description ?? 'The payment did not go through.')),
    );
    rzp.open();
  });
  if (!response) return null;
  return verifyInvoicePayment({
    razorpayOrderId: response.razorpay_order_id,
    razorpayPaymentId: response.razorpay_payment_id,
    razorpaySignature: response.razorpay_signature,
  });
}

// --- Invoice / receipt PDF downloads ---------------------------------------

async function saveBlob(path: string, fallbackName: string): Promise<void> {
  const { blob, fileName } = await apiClient.getBlob(path);
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName ?? fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export type InvoiceDocumentKind = 'pdf' | 'receipt';

/** Downloads an invoice PDF or (paid invoices) its payment receipt; with `tenantId`, the platform-admin copy of that tenant's. */
export function useDownloadInvoiceDocument(tenantId?: string) {
  return useMutation<void, ApiClientError, { invoiceId: string; kind: InvoiceDocumentKind }>({
    mutationFn: ({ invoiceId, kind }) =>
      saveBlob(
        tenantId
          ? `/admin/tenants/${tenantId}/invoices/${invoiceId}/${kind}`
          : `/billing/invoices/${invoiceId}/${kind}`,
        kind === 'pdf' ? 'invoice.pdf' : 'receipt.pdf',
      ),
  });
}

// --- Overdue invoices and suspension ---------------------------------------

/** This workspace's suspension / overdue state - re-checked every 5 minutes. */
export function useBillingStatus(enabled = true) {
  return useQuery<BillingStatus, ApiClientError>({
    queryKey: [...MY_BILLING_KEY, 'status'],
    queryFn: () => apiClient.get<BillingStatus>('/billing/status'),
    enabled,
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
}

const COLLECTIONS_KEY = ['admin', 'billing-collections'] as const;

export function useCollectionsSettings() {
  return useQuery<CollectionsSettings, ApiClientError>({
    queryKey: COLLECTIONS_KEY,
    queryFn: () => apiClient.get<CollectionsSettings>('/admin/billing-collections'),
  });
}

export function useSaveCollectionsSettings() {
  const qc = useQueryClient();
  return useMutation<CollectionsSettings, ApiClientError, CollectionsSettings>({
    mutationFn: (input) => apiClient.put<CollectionsSettings>('/admin/billing-collections', input),
    onSuccess: (data) => qc.setQueryData(COLLECTIONS_KEY, data),
  });
}

export function useTenantBillingStatus(tenantId: string) {
  return useQuery<BillingStatus, ApiClientError>({
    queryKey: ['admin', 'billing-status', tenantId],
    queryFn: () => apiClient.get<BillingStatus>(`/admin/tenants/${tenantId}/billing-status`),
  });
}

export function usePauseSuspension(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<BillingStatus, ApiClientError, number>({
    mutationFn: (days) =>
      apiClient.post<BillingStatus>(`/admin/tenants/${tenantId}/billing-suspension/pause`, {
        days,
      }),
    onSuccess: (data) => qc.setQueryData(['admin', 'billing-status', tenantId], data),
  });
}

// --- Menus included in a plan ----------------------------------------------

export function usePlanMenus(planId: string) {
  return useQuery<{ menuKeys: string[] }, ApiClientError>({
    queryKey: [...PLANS_KEY, planId, 'menus'],
    queryFn: () => apiClient.get<{ menuKeys: string[] }>(`/admin/plans/${planId}/menus`),
  });
}

export function useSetPlanMenus(planId: string) {
  const qc = useQueryClient();
  return useMutation<{ menuKeys: string[]; workspacesUpdated: number }, ApiClientError, string[]>({
    mutationFn: (menuKeys) =>
      apiClient.put<{ menuKeys: string[]; workspacesUpdated: number }>(
        `/admin/plans/${planId}/menus`,
        { menuKeys },
      ),
    onSuccess: (data) => {
      qc.setQueryData([...PLANS_KEY, planId, 'menus'], { menuKeys: data.menuKeys });
      // Workspaces' menu access changed too.
      void qc.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

// --- Plan limits ---------------------------------------------------------------

export function useMyLimits() {
  return useQuery<TenantLimits, ApiClientError>({
    queryKey: [...MY_BILLING_KEY, 'limits'],
    queryFn: () => apiClient.get<TenantLimits>('/billing/limits'),
  });
}

export function useTenantLimits(tenantId: string) {
  return useQuery<TenantLimits, ApiClientError>({
    queryKey: ['admin', 'limits', tenantId],
    queryFn: () => apiClient.get<TenantLimits>(`/admin/tenants/${tenantId}/limits`),
  });
}

export function useSetTenantLimits(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<TenantLimits, ApiClientError, TenantLimitOverridesInput>({
    mutationFn: (overrides) =>
      apiClient.put<TenantLimits>(`/admin/tenants/${tenantId}/limits`, { overrides }),
    onSuccess: (data) => qc.setQueryData(['admin', 'limits', tenantId], data),
  });
}
